/**
 * Chrome & theme icons — the bezel / rail affordances and the theme cycle. Line-only,
 * single-silhouette. Sizes are intrinsic defaults; several call sites size the svg via CSS
 * (e.g. .nav-icon svg { width:18px }) and several rely on a CSS-hook class on the svg for
 * rotation/theming (.nav-chevron, .dropdown-arrow, .type-selector-chevron, .expand-icon…).
 * These icons are deliberately CLASS-FREE — the consumer forwards the hook via className.
 */
import { Icon, type IconProps } from './Icon';

/** Open NORA, the Station Operator — a speech bubble. */
export const Chat = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
  </Icon>
);

/** Expand caret / dropdown. Callers pass the rotation hook class (e.g. "nav-chevron"). */
export const ChevronDown = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M6 9l6 6 6-6" />
  </Icon>
);

/** Collapse the rail / back. */
export const ChevronLeft = (props: IconProps) => (
  <Icon {...props}>
    <path d="M15 18L9 12L15 6" />
  </Icon>
);

/** Forward / drill-in caret (locator rows, disclosure). */
export const ChevronRight = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 6l6 6-6 6" />
  </Icon>
);

/** Collapse-upward caret (two-state disclosure toggles). */
export const ChevronUp = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 15l6-6 6 6" />
  </Icon>
);

/** Menu (hamburger) — three rules. */
export const Hamburger = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 6H20M4 12H20M4 18H20" />
  </Icon>
);

/** Theme: light — sun disc + rays. */
export const ThemeLight = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2V4M12 20V22M4 12H2M22 12H20M5.64 5.64L4.22 4.22M19.78 19.78L18.36 18.36M5.64 18.36L4.22 19.78M19.78 4.22L18.36 5.64" />
  </Icon>
);

/** Theme: dark — crescent moon. */
export const ThemeDark = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
  </Icon>
);

/** Theme: system — monitor with stand. */
export const ThemeSystem = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <rect x="3" y="4" width="18" height="14" rx="2" />
    <path d="M8 21H16M12 18V21" />
  </Icon>
);

/** Map projection: flat — a folded paper map (mercator). */
export const MapFlat = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" />
    <path d="M9 4v14M15 6v14" />
  </Icon>
);

/** Map projection: globe — sphere with meridian + parallel. */
export const Globe = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a13.5 13.5 0 0 1 0 18a13.5 13.5 0 0 1 0-18z" />
  </Icon>
);

/** Sign out — door + arrow. */
export const Logout = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </Icon>
);

/** Settings — gear. */
export const Settings = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Icon>
);
